export enum ContentPageType {
  LANDING_PAGE = 'landing_page',
  CLOSED_PAGE = 'closed_page',
  ORDER_PAGE = 'order_confirmation_page',
  FAQ_PAGE = 'faq_page',
  QUIZ_CUSTOMER_PAGE = 'quiz_customer_page',
  QUIZ_PURCHASE_PAGE = 'quiz_purchased_product_page',
  QUIZ_VALUATION_PAGE = 'quiz_traded_product_page',
  QUIZ_ADDRESS_PAGE = 'quiz_address_page',
  QUIZ_PAYMENT_PAGE = 'quiz_payment_page',
  PARTIAL_FAQ_BANNER = 'faq_banner',
  PARTIAL_QUIZ_TRACKER = 'quiz_tracker',
  TERMS_PAGE = 'terms_and_conditions_page',
}

export interface ContentTrackerStep {
  long_name: string
  short_name: string
}

export interface ContentQuizQuestion {
  label: string
  placeholder: string
  validation_message?: string
}

export interface ContentQuizBase {
  title: string
  intro: string
  button_text: string
}

export interface ContentDocumentData {
  title: string
  description: string
}

export type ContentButtonType = 'relative' | 'absolute'

export interface TopicQuestion {
  question: string
  answer: string
}

export interface TopicData {
  id: string
  title: string
  icon: string | File
  questions: TopicQuestion[]
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/landing_page` endpoint.
 */
export interface LandingPageContentPayload {
  document_data: ContentDocumentData
  banner: {
    title: string
    pre_title: string
    intro: string
    button_text: string
    image: string
  }
  step_list: {
    title: string
    small_print: string
    button_text: string
    steps: Array<{
      content: string
      image: string
    }>
  }
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/closed_page` endpoint.
 */
export interface ClosedPageContentPayload {
  document_data: ContentDocumentData
  banner: {
    title: string
    intro: string
    outro: string
    image: File | string
    button_text: string
    button_href: string
    button_type: ContentButtonType
  }
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/order_confirmation_page` endpoint.
 */
export interface OrderPageContentPayload {
  document_data: ContentDocumentData
  banner: {
    title: string
  }
  step_list: {
    title: string
    steps: Array<{
      content: string
      footnote?: string
    }>
  }
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/faq_page` endpoint.
 */
export interface FAQPageContentPayload {
  document_data: ContentDocumentData
  search: {
    title: string
    placeholder_text: string
  }
  topic_list: {
    title: string
    topics: Array<TopicData>
  }
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/quiz_customer_page` endpoint.
 */
export interface CustomerPageContentPayload {
  document_data: ContentDocumentData
  base: ContentQuizBase
  questions: {
    customer_name: ContentQuizQuestion
    customer_email: ContentQuizQuestion
  }
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/quiz_purchased_products_page` endpoint.
 */
export interface PurchasePageContentPayload {
  document_data: ContentDocumentData
  base: ContentQuizBase
  questions: {
    purchased_model: Omit<ContentQuizQuestion, 'placeholder'>
    serial_number_file_upload: ContentQuizQuestion
    serial_number: ContentQuizQuestion
    receipt_file_upload: ContentQuizQuestion
    date_of_purchase: ContentQuizQuestion
    place_of_purchase: ContentQuizQuestion
  }
  serial_number_dialog: {
    open_dialog: string
    close_dialog: string
    items: Array<{
      id: string
      content: string
      image: File | string
    }>
  }
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/quiz_traded_products_page` endpoint.
 */
export interface ValuationPageContentPayload {
  document_data: ContentDocumentData
  base: ContentQuizBase
  questions: {
    traded_product_manufacturer: {
      question: string
    }
    traded_product_model?: Omit<ContentQuizQuestion, 'placeholder'>
    agree_to_terms: {
      question: string
      checkbox_label: string
      terms_summary: string
      input_error: string
    }
  }
  offer_dialog: {
    title: string
    description: string
    button_text: string
  }
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/quiz_address_page` endpoint.
 */
export interface AddressPageContentPayload {
  document_data: ContentDocumentData
  base: ContentQuizBase
  questions: {
    lookup_address: {
      question: string
      placeholder: string
      manual_address_prompt: string
    }
    manual_enter_address: {
      street_address_label: string
      city_label: string
      postal_code_label: string
      country_label: string
    }
  }
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/quiz_payment_page` endpoint.
 */
export interface PaymentPageContentPayload {
  document_data: ContentDocumentData
  base: ContentQuizBase
  questions: {
    account_holder: ContentQuizQuestion
    iban: ContentQuizQuestion
    agree_to_terms: {
      checkbox_label: string
    }
  }
  confirmation_dialog: {
    title: string
    intro: string
    cancel_button: string
    confirm_button: string
  }
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/faq_banner` endpoint.
 */
export interface FAQPartialContentPayload {
  title: string
  image: string
  button_text: string
}

/**
 * Payload for the `PUT /programmes/{*}/campaigns/{*}/contents/quiz_tracker` endpoint.
 */
export interface QuizTrackerPartialContentPayload {
  purchase_step: ContentTrackerStep
  valuation_step: ContentTrackerStep
  address_step: ContentTrackerStep
  payment_step: ContentTrackerStep
}

/**
 * Union type for all possible content payloads and responses.
 */
export type GetCampaignContentResponse =
  | LandingPageContentPayload
  | ClosedPageContentPayload
  | OrderPageContentPayload
  | FAQPageContentPayload
  | CustomerPageContentPayload
  | PurchasePageContentPayload
  | ValuationPageContentPayload
  | AddressPageContentPayload
  | PaymentPageContentPayload
  | FAQPartialContentPayload
  | QuizTrackerPartialContentPayload
